import React, { Component } from 'react';
import { withApollo } from 'react-apollo';
import { withNamespaces } from 'react-i18next';
import { withStyles } from '@material-ui/core/styles';
import classnames from 'classnames';
import _findIndex from 'lodash/findIndex';
import _isNumber from 'lodash/isNumber';
import _filter from 'lodash/filter';

import Button from '@material-ui/core/Button';

import { Table as DvExTable } from '@devexpress/dx-react-grid-material-ui';
import Table from '../../components/Global/Table/Table';
import FileTableMetadata from './FileTable.metadata';
import ErrorNotification from '../../components/common/ErrorNotification';

import FileUpload from '../../components/Global/FileUpload/FileUpload';
import AssignerModal from '../../components/file/Assigner';

import combineStyles from '../../utils/combineStyles';
import LayoutStyles from '../../styles/layout';
import ButtonStyles from '../../styles/button';
import TypographyStyles from '../../styles/typography';
import SpacingStyles from '../../styles/helpers/spacing';

import { DEFAULT_ROWS_PER_PAGE } from '../../constants/constants';

import { AppContext } from '../../AppProvider';

import {
  unassignedFiles as unassignedFilesQuery,
  shipmentIds as shipmentIdsQuery
} from '../../graphQL/queries/shipmentQueries';
import { sanitize } from '../../Models/File';

import {
  updateFile as updateFileMutation,
  deleteFile as deleteFileMutation
} from '../../graphQL/mutations/shipmentMutations';

const styles = theme => ({
  titleWrapper: {
    alignItems: 'center',
    display: 'flex',
    marginBottom: theme.spacing.unit * 5
  },
  title: {
    flex: 1
  },
  tableRow: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: 'rgba(12,31,44, 0.04)'
    }
  }
});

export class FilesIndex extends Component {
  state = {
    rowsSelected: [],
    items: [],
    allItems: [],
    item: {},
    loading: true,
    loadingMoreFiles: false,
    error: null,
    errorsOnSave: null,
    contact: null,
    isSubmitting: false,
    page: 0,
    totalCount: 0,
    rowsPerPage: localStorage.getItem('PropelRowsPerPage')
      ? parseInt(localStorage.getItem('PropelRowsPerPage'), 10)
      : DEFAULT_ROWS_PER_PAGE,
    deleting: false,
    isOpenFileUploader: false,
    isOpenFileAssigner: false,
    hasUpdated: false,
    shipmentIds: []
  };

  async componentDidMount() {
    await this._getShipmentIds();
    await this._getList();
  }

  _getList = async () => {
    const { client: ApolloClient } = this.props;
    const { currentOrgId } = this.context.globalProps;
    const { page, rowsPerPage } = this.state;
    const offset = page * rowsPerPage;

    try {
      const {
        data: {
          shipment: {
            unassignedFiles: { results: items, totalCount }
          }
        }
      } = await ApolloClient.query({
        query: unassignedFilesQuery,
        variables: {
          orgId: currentOrgId,
          offset,
          limit: rowsPerPage
        },
        fetchPolicy: 'network-only'
      });

      this.setState({
        items: FileTableMetadata.formatData(items),
        totalCount: _isNumber(totalCount) ? totalCount : this.state.totalCount,
        hasUpdated: false
      });
    } catch (error) {
      this.setState({
        error
      });
    } finally {
      this.setState({
        loading: false
      });
    }
  };

  _getShipmentIds = async () => {
    const { client: ApolloClient } = this.props;
    const { currentOrgId } = this.context.globalProps;

    try {
      const {
        data: {
          shipment: { all: shipmentIds }
        }
      } = await ApolloClient.query({
        query: shipmentIdsQuery,
        variables: {
          orgId: currentOrgId
        }
      });

      this.setState({
        shipmentIds
      });
    } catch (error) {
      this.setState({
        error
      });
    }
  };

  _handleRowClick = row => {
    this.setState({
      isOpenFileAssigner: true,
      currentItemIndex:
        _findIndex(this.state.items, { id: row.id }) +
        this.state.rowsPerPage * this.state.page,
      item: row
    });
  };

  _handleSelectRow = rowsSelected => this.setState({ rowsSelected });

  _handleDelete = async () => {
    this.setState({ deleting: true });
    const { client: ApolloClient } = this.props;

    const { items, rowsSelected } = this.state;
    const itemsSelected = _filter(items, (item, index) =>
      rowsSelected.includes(index)
    );
    const itemsIdSelected = itemsSelected.map(item => item.id);
    try {
      await Promise.all(
        itemsIdSelected.map(
          async itemId =>
            await ApolloClient.mutate({
              mutation: deleteFileMutation,
              variables: { shipmentFileId: parseInt(itemId) }
            })
        )
      );
      await this._getList();
      this._getAvailableAccessorials();
    } catch (error) {
      // this.setState({error});
    } finally {
      this.setState({ deleting: false, rowsSelected: [] });
    }
  };

  _handleAddFile = () => this.setState({ isOpenFileUploader: true });

  _handleCloseFileUploader = () =>
    this.setState(
      { isOpenFileUploader: false, page: 0, loading: true },
      this._getList
    );

  _handleCloseFileAssigner = () =>
    this.setState(
      {
        isOpenFileAssigner: false,
        page: this.state.hasUpdated ? 0 : this.state.page,
        loading: this.state.hasUpdated
      },
      async () => {
        if (this.state.hasUpdated) {
          await this._getList();
        }
      }
    );

  _handleNavigate = itemIndex => {
    // eslint-disable-next-line
    const [_number, strPage, strItemIndex = 0] = String(
      itemIndex / this.state.rowsPerPage
    ).match(/(\d+)(\.\d+)?/);

    const page = parseInt(strPage);
    const item = parseInt(
      parseFloat(strItemIndex || 0) * this.state.rowsPerPage
    );

    if (page !== this.state.page) {
      this.setState({ page, loadingMoreFiles: true }, async () => {
        await this._getList();
        this.setState({
          currentItemIndex: itemIndex,
          item: this.state.items[item],
          loadingMoreFiles: false
        });
      });
    } else {
      this.setState({
        currentItemIndex: itemIndex,
        item: this.state.items[item]
      });
    }
  };

  _handleAssign = async values => {
    const { client: ApolloClient } = this.props;
    try {
      this.setState({ isSubmitting: true });
      await ApolloClient.mutate({
        mutation: updateFileMutation,
        variables: {
          file: sanitize(values)
        }
      });

      const items = [...this.state.items];
      const itemposition = _findIndex(items, item => item.id === values.id);
      items[itemposition] = { ...values };

      this.setState({ items: [...items], hasUpdated: true });
    } catch (error) {
      this.setState({ errorsOnSave: error });
    } finally {
      this.setState({ isSubmitting: false });
    }
  };

  _changePage = page => {
    this.setState(
      {
        page,
        loading: true,
        currentItemIndex: this.state.rowsPerPage * (page + 1) + 1
      },
      this._getList
    );
  };

  _changeRowsPerPage = rowsPerPage => {
    localStorage.setItem('PropelRowsPerPage', rowsPerPage);
    this.setState({ rowsPerPage: rowsPerPage, page: 0 });
    const rows = rowsPerPage === 0 ? DEFAULT_ROWS_PER_PAGE : rowsPerPage;
    this.setState({ rowsPerPage: rows, page: 0, loading: true }, this._getList);
  };

  render() {
    const { t, classes } = this.props;

    if (this.state.error) {
      return <ErrorNotification message="Error loading page" />;
    }

    return (
      <div>
        <div className={classes.titleWrapper}>
          <h1
            className={classnames(
              classes.headline4,
              classes.noMarginBottom,
              classes.title
            )}
          >
            {t('File Inbox')}
          </h1>
          <Button
            className={classes.button}
            classes={{
              label: classes.buttonLabel
            }}
            variant="contained"
            color="primary"
            onClick={this._handleAddFile}
          >
            {t('Add Files')}
          </Button>
        </div>
        <div>
          <Table
            name={t('File')}
            columns={FileTableMetadata.columns({ t })}
            data={this.state.items}
            rowComponent={props => (
              <DvExTable.Row
                {...props}
                className={classes.tableRow}
                onClick={() => this._handleRowClick(props.row)}
              />
            )}
            rowsPerPage={this.state.rowsPerPage}
            totalCount={this.state.totalCount}
            loading={this.state.loading}
            withDownload={false}
            withColumnChooser={false}
            withSelection={true}
            onChangePage={this._changePage}
            onChangePageRowsPerPage={this._changeRowsPerPage}
            rowsSelected={this.state.rowsSelected}
            onSelectRow={this._handleSelectRow}
            onDelete={this._handleDelete}
            deleting={this.state.deleting}
            page={this.state.page}
          />
        </div>
        <FileUpload
          open={this.state.isOpenFileUploader}
          onClose={this._handleCloseFileUploader}
        />
        {this.state.isOpenFileAssigner && (
          <AssignerModal
            onNavigate={this._handleNavigate}
            onClose={this._handleCloseFileAssigner}
            currentItemIndex={this.state.currentItemIndex}
            totalItems={this.state.totalCount}
            item={this.state.item}
            onSave={this._handleAssign}
            isSubmitting={this.state.isSubmitting}
            loadingMoreFiles={this.state.loadingMoreFiles}
            shipmentIds={this.state.shipmentIds}
          />
        )}
        {this.state.errorsOnSave && (
          <ErrorNotification message={this.state.errorsOnSave.message} />
        )}
      </div>
    );
  }
}

FilesIndex.contextType = AppContext;
const FilesIndexWithTranslation = withNamespaces()(FilesIndex);
export default withStyles(
  combineStyles(
    LayoutStyles,
    ButtonStyles,
    TypographyStyles,
    SpacingStyles,
    styles
  )
)(withApollo(FilesIndexWithTranslation));
